import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Error from "../components/Error";
import Card from "../components/Card";
import ScrollingTips from "../components/ScrollingTips";
import { SkeletonGrid } from "../components/Skeleton";
import { fetchEvents } from "../api";
import { notFinishedClause } from "../utils/searchFilters";
import "./Home.css";

const PAGE_SIZE = 6;

const tips = [
  "Consultez Getout Paname sur mobile pour garder toutes les infos à portée de main.",
  "Enregistrez un événement avec le signet : votre sélection est conservée à votre retour.",
  "Du mal à localiser le lieu ? Une carte interactive est disponible pour la plupart des événements.",
  "Des pictogrammes signalent les événements accessibles aux personnes en situation de handicap.",
];

// One tap from the home page into a pre-filtered search.
const BROWSE = [
  { label: "Ce soir", to: "/search?when=today" },
  { label: "Ce week-end", to: "/search?when=weekend" },
  { label: "Gratuit", to: "/search?price=free" },
  { label: "Concerts", to: "/search?cats=music" },
  { label: "Expos", to: "/search?cats=expo" },
  { label: "En famille", to: "/search?cats=family" },
  { label: "Ateliers", to: "/search?cats=workshop" },
];

function useEventList(orderBy, where) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setIsLoaded(false);
    setError(null);
    fetchEvents({ limit: PAGE_SIZE, orderBy, where }, ac.signal)
      .then((result) => {
        setItems(result.records.map((item) => item.record));
        setIsLoaded(true);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setIsLoaded(true);
      });
    return () => ac.abort();
  }, [orderBy, where, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);
  return { items, error, isLoaded, retry };
}

function Section({ title, intro, list, ctaTo, ctaLabel }) {
  return (
    <section className="pa-section">
      <div className="pa-section__head">
        <div className="pa-section__headings">
          <h2>{title}</h2>
          <p className="pa-lead pa-section__intro">{intro}</p>
        </div>
        <Link to={ctaTo} className="pa-link pa-section__cta">
          {ctaLabel}
          <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
        </Link>
      </div>

      {list.error && <Error err={list.error} onRetry={list.retry} />}
      {!list.isLoaded && !list.error && <SkeletonGrid count={3} />}
      {!list.error && list.isLoaded && (
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 pa-grid">
          {list.items.map(
            (item) =>
              item && (
                <div className="col" key={item.id}>
                  <Card content={item} />
                </div>
              )
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Le héros montre les affiches des prochains événements plutôt qu'une photo
 * de décoration : c'est le produit qui se montre lui-même, et ça ne vieillit
 * jamais. La liste est ordonnée par date de début, donc différente des deux
 * sections du dessous, pour ne pas remontrer les mêmes visuels.
 */
function Hero({ list }) {
  const affiches = (list?.items || []).filter((e) => e.fields?.cover_url).slice(0, 4);
  // Quatre ou rien : une mosaïque incomplète se lit comme un chargement raté.
  const pret = list?.isLoaded && affiches.length === 4;

  return (
    <section className="pa-hero">
      <div className="pa-hero__text">
        <p className="pa-eyebrow">Agenda culturel de la Ville de Paris</p>
        <h1 className="pa-display pa-hero__title">
          Paris, <span className="pa-display__accent">tous les jours.</span>
        </h1>
        <p className="pa-lead pa-hero__lead">
          Concerts, expositions, théâtre, ateliers, balades : toute la
          programmation culturelle de la capitale, au même endroit.
        </p>
        <div className="pa-hero__actions">
          <Link to="/search" className="pa-btn pa-btn--primary pa-btn--lg">
            <i className="bi bi-search" aria-hidden="true"></i>
            Rechercher un événement
          </Link>
          <Link to="/saved" className="pa-btn pa-btn--ghost pa-btn--lg">
            <i className="bi bi-bookmark" aria-hidden="true"></i>
            Mes favoris
          </Link>
        </div>
      </div>

        <div className="pa-hero__media">
          {pret ? (
            <ul className="pa-hero__grid">
              {affiches.map((e) => (
                <li key={e.id}>
                  <Link
                    to={`/event/${e.id}`}
                    className="pa-hero__tile"
                    title={e.fields.title}
                  >
                    <img src={e.fields.cover_url} alt="" loading="lazy" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="pa-hero__grid pa-hero__grid--attente" aria-hidden="true" />
          )}
        </div>

    </section>
  );
}

function Browse() {
  return (
    <nav className="pa-browse" aria-label="Parcourir par envie">
      <span className="pa-label pa-browse__label">Parcourir par envie</span>
      <div className="pa-chip-row">
        {BROWSE.map((b) => (
          <Link key={b.to} to={b.to} className="pa-chip">
            {b.label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

function Home() {
  // Only keep events that have not finished yet.
  const notFinished = useMemo(() => notFinishedClause(), []);

  const endingSoon = useEventList("date_end asc,title asc", notFinished);
  const latest = useEventList("updated_at desc", notFinished);
  // Pour le héros : ce qui commence le plus tôt, donc d'autres visuels que
  // « se termine bientôt » et « nouveautés ».
  const soonest = useEventList("date_start asc,title asc", notFinished);

  return (
    <div className="container">
      <Hero list={soonest} />
      <Browse />

      <Section
        title="À ne pas manquer"
        intro="Ces événements se terminent très bientôt."
        list={endingSoon}
        ctaTo="/search?when=week"
        ctaLabel="Voir cette semaine"
      />

      <Section
        title="C'est tout frais"
        intro="Les dernières nouveautés de l'agenda."
        list={latest}
        ctaTo="/search?sort=recent"
        ctaLabel="Tout parcourir"
      />

      <ScrollingTips tips={tips} />
    </div>
  );
}

export default Home;
